import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import defaultContent from './defaultContent.json';

const CMS_STORAGE_PATH = 'cms_data/content.json';
const CMS_CDN_URL = 'https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/cms_data%2Fcontent.json?alt=media';

// In-memory cache
let cachedData = null;

// Always fetch fresh content from Firebase Storage CDN with cache-busting timestamp
export async function loadFullContent(forceRefresh = false) {
  if (cachedData && !forceRefresh) return cachedData;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);
    const res = await fetch(`${CMS_CDN_URL}&ts=${Date.now()}`, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      cachedData = { ...defaultContent, ...data };
      try {
        localStorage.setItem('lovingarms_cms_store', JSON.stringify(cachedData));
      } catch (e) {}
      return cachedData;
    }
  } catch (err) {
    console.warn('Network fetch of CMS content failed, falling back to local store:', err.message);
  }

  // Fallback to local storage
  try {
    const local = localStorage.getItem('lovingarms_cms_store');
    if (local) {
      const parsed = JSON.parse(local);
      if (parsed?.gallery?.length > 0) {
        cachedData = parsed;
        return cachedData;
      }
    }
  } catch (e) {}

  cachedData = { ...defaultContent };
  return cachedData;
}

// Save full store to Cloud Storage and local cache
export async function saveFullContent(data) {
  cachedData = data;
  try {
    localStorage.setItem('lovingarms_cms_store', JSON.stringify(data));
  } catch (e) {}

  try {
    const fileRef = ref(storage, CMS_STORAGE_PATH);
    const jsonBlob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    await uploadBytes(fileRef, jsonBlob);
  } catch (err) {
    console.error('Error saving CMS content to storage:', err);
  }
  return data;
}

// ============================================================================
// SINGLE DOC METHODS
// ============================================================================

export async function getSiteSettings() {
  const store = await loadFullContent();
  return store.siteSettings || defaultContent.siteSettings;
}

export async function updateSiteSettings(data) {
  const store = await loadFullContent();
  store.siteSettings = { ...store.siteSettings, ...data };
  await saveFullContent(store);
  return store.siteSettings;
}

export async function getHeroContent() {
  const store = await loadFullContent();
  return store.heroContent || defaultContent.heroContent;
}

export async function updateHeroContent(data) {
  const store = await loadFullContent();
  store.heroContent = { ...store.heroContent, ...data };
  await saveFullContent(store);
  return store.heroContent;
}

export async function getAboutContent() {
  const store = await loadFullContent();
  return store.aboutContent || defaultContent.aboutContent;
}

export async function updateAboutContent(data) {
  const store = await loadFullContent();
  store.aboutContent = { ...store.aboutContent, ...data };
  await saveFullContent(store);
  return store.aboutContent;
}

export async function getConstructionContent() {
  const store = await loadFullContent();
  return store.constructionContent || defaultContent.constructionContent;
}

export async function updateConstructionContent(data) {
  const store = await loadFullContent();
  store.constructionContent = { ...store.constructionContent, ...data };
  await saveFullContent(store);
  return store.constructionContent;
}

export async function getDonateContent() {
  const store = await loadFullContent();
  return store.donateContent || defaultContent.donateContent;
}

export async function updateDonateContent(data) {
  const store = await loadFullContent();
  store.donateContent = { ...store.donateContent, ...data };
  await saveFullContent(store);
  return store.donateContent;
}

export async function getContactInfo() {
  const store = await loadFullContent();
  return store.contactInfo || defaultContent.contactInfo;
}

export async function updateContactInfo(data) {
  const store = await loadFullContent();
  store.contactInfo = { ...store.contactInfo, ...data };
  await saveFullContent(store);
  return store.contactInfo;
}

// ============================================================================
// LIST METHODS (Gallery, Team, Impact, Testimonials, Navigation)
// ============================================================================

export async function getGallery() {
  const store = await loadFullContent();
  return store.gallery || [];
}

export async function addGalleryItem(item) {
  const store = await loadFullContent();
  const newItem = { id: String(Date.now()), ...item };
  store.gallery = [...(store.gallery || []), newItem];
  await saveFullContent(store);
  return newItem;
}

export async function updateGalleryItem(id, data) {
  const store = await loadFullContent();
  store.gallery = (store.gallery || []).map(g => (g.id === id ? { ...g, ...data } : g));
  await saveFullContent(store);
  return data;
}

export async function deleteGalleryItem(id) {
  const store = await loadFullContent();
  store.gallery = (store.gallery || []).filter(g => g.id !== id);
  await saveFullContent(store);
  return id;
}

export async function getTeam() {
  const store = await loadFullContent();
  return store.team || [];
}

export async function addTeamMember(item) {
  const store = await loadFullContent();
  const newItem = { id: String(Date.now()), ...item };
  store.team = [...(store.team || []), newItem];
  await saveFullContent(store);
  return newItem;
}

export async function updateTeamMember(id, data) {
  const store = await loadFullContent();
  store.team = (store.team || []).map(t => (t.id === id ? { ...t, ...data } : t));
  await saveFullContent(store);
  return data;
}

export async function deleteTeamMember(id) {
  const store = await loadFullContent();
  store.team = (store.team || []).filter(t => t.id !== id);
  await saveFullContent(store);
  return id;
}

export async function getImpactStats() {
  const store = await loadFullContent();
  return store.impactStats || [];
}

export async function addImpactStat(item) {
  const store = await loadFullContent();
  const newItem = { id: String(Date.now()), ...item };
  store.impactStats = [...(store.impactStats || []), newItem];
  await saveFullContent(store);
  return newItem;
}

export async function updateImpactStat(id, data) {
  const store = await loadFullContent();
  store.impactStats = (store.impactStats || []).map(s => (s.id === id ? { ...s, ...data } : s));
  await saveFullContent(store);
  return data;
}

export async function deleteImpactStat(id) {
  const store = await loadFullContent();
  store.impactStats = (store.impactStats || []).filter(s => s.id !== id);
  await saveFullContent(store);
  return id;
}

export async function getTestimonials() {
  const store = await loadFullContent();
  return store.testimonials || [];
}

export async function addTestimonial(item) {
  const store = await loadFullContent();
  const newItem = { id: String(Date.now()), ...item };
  store.testimonials = [...(store.testimonials || []), newItem];
  await saveFullContent(store);
  return newItem;
}

export async function updateTestimonial(id, data) {
  const store = await loadFullContent();
  store.testimonials = (store.testimonials || []).map(t => (t.id === id ? { ...t, ...data } : t));
  await saveFullContent(store);
  return data;
}

export async function deleteTestimonial(id) {
  const store = await loadFullContent();
  store.testimonials = (store.testimonials || []).filter(t => t.id !== id);
  await saveFullContent(store);
  return id;
}

export async function getNavigationLinks() {
  const store = await loadFullContent();
  return store.navLinks || [];
}

export async function addNavigationLink(item) {
  const store = await loadFullContent();
  const newItem = { id: String(Date.now()), ...item };
  store.navLinks = [...(store.navLinks || []), newItem];
  await saveFullContent(store);
  return newItem;
}

export async function updateNavigationLink(id, data) {
  const store = await loadFullContent();
  store.navLinks = (store.navLinks || []).map(n => (n.id === id ? { ...n, ...data } : n));
  await saveFullContent(store);
  return data;
}

export async function deleteNavigationLink(id) {
  const store = await loadFullContent();
  store.navLinks = (store.navLinks || []).filter(n => n.id !== id);
  await saveFullContent(store);
  return id;
}

// Media upload helpers
export async function uploadImage(file, folder = 'uploads') {
  try {
    const fileRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
    const snapshot = await uploadBytes(fileRef, file);
    return await getDownloadURL(snapshot.ref);
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

export const uploadMedia = uploadImage;

export async function deleteMedia(url) {
  try {
    if (!url) return;
    const fileRef = ref(storage, url);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting media:', error);
  }
}

export async function forceInitDefaultData() {
  await saveFullContent({ ...defaultContent });
  return true;
}

export async function initDefaultData() {
  await loadFullContent(true);
}
